import { FC, ReactNode } from 'react';
import Modal from 'react-modal';
import { LogoutLoader } from './loaders';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  type?: 'primary' | 'danger' | 'warning' | 'info' | 'success';
}

const CONFIRM_BUTTON_CLASS: Record<NonNullable<ConfirmationModalProps['type']>, string> = {
  primary: 'bg-primary hover:opacity-90',
  danger: 'bg-red-600 hover:opacity-90',
  warning: 'bg-warning-600 hover:opacity-90',
  info: 'bg-blue-600 hover:opacity-90',
  success: 'bg-success-400 hover:opacity-90',
};

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  type = 'danger',
}) => {
  const confirmColor = CONFIRM_BUTTON_CLASS[type];

  return (
    <Modal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={onClose}
      overlayClassName="fixed inset-0 bg-black/30 z-[600] flex items-center justify-center"
      className="outline-none"
    >
      <div className="bg-white rounded-2xl w-[480px] max-w-[90vw] p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="bold text-xl text-text">{title}</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <p className="text-sm text-text leading-relaxed mb-6">{message}</p>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-6 py-2.5 text-sm medium border border-slate-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-6 py-2.5 text-sm medium rounded-lg text-white transition-colors flex items-center justify-center min-w-[100px] ${
              isLoading ? `${confirmColor} opacity-75 cursor-not-allowed` : confirmColor
            }`}
          >
            {isLoading ? <LogoutLoader /> : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};
